import React, { useMemo } from "react";
import { Alert, Button, Checkbox, Empty, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { BankFloorOpening } from "../../utils/bankFloorOpenings";
import { describeFloors } from "./floorSpec";
import {
  controlDoorSide,
  eligibleFloorPositions,
  type DoorSide,
} from "../../utils/controlFloorRules";

/**
 * Per-floor opening grid for a scheduled action's target floors.
 *
 * Replaces the raw interval expression (`[1,5),10`) as the DEFAULT way to name
 * floors. The expression is not gone — it moves behind the editor's "Advanced"
 * toggle — but it required an operator to hold the bank's floor positions in
 * their head and to know that `[1,5)` excludes 5. The grid shows the floors the
 * bank actually has, and which openings each of them actually has.
 *
 * WHICH COLUMN IS LIVE IS DECIDED BY THE CONTROL, not by the checkbox. A
 * TimedAction stores one `start_message_value`, and front/rear is encoded in
 * that Control itself — `CarCallLock` is the front car call, `RearCarCallLock`
 * the rear one. So a front command can only ever contribute front floors. The
 * opposite column is still RENDERED, greyed with an explanation, rather than
 * hidden: an operator who wants rear floors needs to learn that the fix is to
 * pick the rear command, and a column that simply vanished would not teach that.
 *
 * The eligibility itself is `eligibleFloorPositions` — the same function the
 * wizard's action seeder uses. Sharing it is the point: a picker that offered a
 * floor the seeder considers ineligible would let an operator schedule a command
 * the bank cannot act on.
 */
export interface FloorOpeningPickerProps {
  /**
   * The floors to offer, already unioned across the selected banks. An empty
   * array means "no floor data", which the caller must handle by falling back to
   * the Advanced expression — the grid cannot express a floor it cannot show.
   */
  floors: BankFloorOpening[];
  /**
   * The chosen Control enum value. `undefined` while the form has not yet
   * reported one; the grid then renders read-only rather than guessing a side.
   *
   * NOT nullable-by-falsiness anywhere below: `Control.CarCallLock` is 0 and is
   * a front-door, per-floor command, so a `!control` check would silently make
   * the commonest lock command unselectable.
   */
  control: number | undefined;
  /** Currently selected floor positions. */
  value: number[];
  onChange: (positions: number[]) => void;
  disabled?: boolean;
}

/** The two opening columns, in render order. */
const SIDES: Exclude<DoorSide, "any">[] = ["front", "rear"];

interface SideState {
  /** Positions this side can contribute, given the control and the floor data. */
  selectable: Set<number>;
  /** True when this side is the one the chosen control acts on. */
  live: boolean;
}

function FloorOpeningPicker({
  floors,
  control,
  value,
  onChange,
  disabled = false,
}: FloorOpeningPickerProps): React.ReactElement {
  const [t] = useTranslation("global");

  const selected = useMemo(() => new Set(value), [value]);

  const rows = useMemo(() => [...floors].sort((a, b) => a.position - b.position), [floors]);

  /**
   * Per-side eligibility.
   *
   * `eligibleFloorPositions` answers for the CONTROL's own side (it reads
   * `controlDoorSide` internally), so it is asked once and its answer belongs to
   * that side; the other side is live only for a door-agnostic control.
   * `ShuttleFloor` is the one floor-based Control that is — it acts on the floor
   * as a whole — and `controlFloorRules`' drift-guard test names it as the only
   * exemption from the front/rear rule.
   */
  const sideStates = useMemo((): Record<"front" | "rear", SideState> => {
    const empty: SideState = { selectable: new Set<number>(), live: false };
    if (control === undefined) return { front: empty, rear: empty };

    const side = controlDoorSide(control);
    const eligible = new Set(eligibleFloorPositions(control, rows));

    const stateFor = (column: "front" | "rear"): SideState => {
      if (side === column) return { selectable: eligible, live: true };
      if (side !== "any") return empty;

      // Door-agnostic: the command lands wherever the opening exists, so each
      // column is limited by the floors that actually have it — and still by the
      // shared eligibility, so a direction's top/bottom exclusion holds here too.
      return {
        selectable: new Set(
          rows.filter((f) => f[column] && eligible.has(f.position)).map((f) => f.position),
        ),
        live: true,
      };
    };

    return { front: stateFor("front"), rear: stateFor("rear") };
  }, [control, rows]);

  const toggleFloor = (position: number, checked: boolean): void => {
    const next = new Set(selected);
    if (checked) next.add(position);
    else next.delete(position);
    onChange(Array.from(next).sort((a, b) => a - b));
  };

  /**
   * Select-all / deselect-all for one side.
   *
   * The selection is ONE set of positions shared by both columns, because the
   * stored model is one floor set. So "all front" unions the front-selectable
   * positions in, and "none" removes exactly those — leaving any position the
   * other column contributed untouched rather than clearing the whole field.
   */
  const toggleSide = (side: "front" | "rear", checked: boolean): void => {
    const { selectable } = sideStates[side];
    const next = new Set(selected);
    selectable.forEach((position) => {
      if (checked) next.add(position);
      else next.delete(position);
    });
    onChange(Array.from(next).sort((a, b) => a - b));
  };

  const selectedCountFor = (side: "front" | "rear"): number => {
    let count = 0;
    sideStates[side].selectable.forEach((position) => {
      if (selected.has(position)) count += 1;
    });

    return count;
  };

  /**
   * Selected floors the CURRENT command cannot act on.
   *
   * These arise the moment an operator switches the command: floors picked for
   * `CarCallLock` stay in the value after switching to `RearCarCallLock`, but
   * their column is now inert, so they cannot be unticked — a floor set that is
   * both wrong and unreachable. They also arrive from data written before this
   * grid existed, where nothing checked the opening.
   *
   * They are NOT pruned automatically. Silently rewriting a stored floor set the
   * moment an editor opens is exactly the kind of invisible edit that makes an
   * operator distrust the screen; the banner below states the problem and offers
   * the fix as one deliberate click.
   */
  // Guarded on the control being KNOWN. With none reported yet both sides are
  // empty by construction, so every selected floor would look unreachable and
  // the banner would offer to delete a perfectly valid selection — for a command
  // that has not been chosen. `undefined` means "not reported", which is exactly
  // not "cannot act on"; the header checkbox already draws that distinction.
  const stranded =
    control === undefined
      ? []
      : Array.from(selected).filter(
          (position) =>
            !sideStates.front.selectable.has(position) && !sideStates.rear.selectable.has(position),
        );

  const dropStranded = (): void => {
    const strandedSet = new Set(stranded);
    onChange(Array.from(selected).filter((position) => !strandedSet.has(position)));
  };

  if (rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t(
          "scheduledActions.floorGridNoFloors",
          "No floor list for the selected banks. Use Advanced to enter a floor range.",
        )}
      />
    );
  }

  const sideLabel = (side: "front" | "rear"): string =>
    side === "front"
      ? t("scheduledActions.floorGridFront", "Front")
      : t("scheduledActions.floorGridRear", "Rear");

  const renderHeaderCheckbox = (side: "front" | "rear"): React.ReactElement => {
    const { selectable, live } = sideStates[side];
    const total = selectable.size;
    const count = selectedCountFor(side);
    const header = (
      <Checkbox
        // The header checkbox IS the select-all / deselect-all control: it
        // checks every selectable floor of its column when it is not already
        // full, and clears them when it is. Indeterminate marks a partial
        // selection so "some are on" never looks like "none are on".
        indeterminate={count > 0 && count < total}
        checked={total > 0 && count === total}
        disabled={disabled || !live || total === 0}
        onChange={(e) => toggleSide(side, e.target.checked)}
        data-testid={`floor-grid-all-${side}`}
        // Named distinctly from the floor checkboxes below it, which are also
        // called "Front"/"Rear". Without this a screen reader announces the
        // column header and every cell under it identically.
        aria-label={t(
          "scheduledActions.floorGridSelectAll",
          "Select all {{side}} floors ({{count}} of {{total}} selected)",
          { side: sideLabel(side).toLowerCase(), count, total },
        )}
      >
        {sideLabel(side)}
      </Checkbox>
    );

    if (live || control === undefined) return header;

    return (
      <Tooltip
        title={t(
          "scheduledActions.floorGridWrongSide",
          "This command acts on the other side of the car. Choose the matching {{side}} command to target {{side}} openings.",
          { side: sideLabel(side).toLowerCase() },
        )}
      >
        <span>{header}</span>
      </Tooltip>
    );
  };

  const renderCell = (floor: BankFloorOpening, side: "front" | "rear"): React.ReactElement => {
    const hasOpening = side === "front" ? floor.front : floor.rear;
    if (!hasOpening) {
      return (
        <Tooltip
          title={t("scheduledActions.floorGridNoOpening", "This floor has no {{side}} opening.", {
            side: sideLabel(side).toLowerCase(),
          })}
        >
          <Typography.Text type="secondary">—</Typography.Text>
        </Tooltip>
      );
    }

    const { selectable, live } = sideStates[side];
    const eligible = selectable.has(floor.position);

    return (
      <Checkbox
        checked={selected.has(floor.position)}
        disabled={disabled || !live || !eligible}
        onChange={(e) => toggleFloor(floor.position, e.target.checked)}
        // The position is ALWAYS appended, not just when the floor is unnamed:
        // merging floors across banks makes two "Lobby"s realistic, and two
        // controls with the same accessible name are indistinguishable.
        aria-label={
          floor.name === undefined
            ? `${sideLabel(side)} ${floor.position}`
            : `${sideLabel(side)} ${floor.name} (${floor.position})`
        }
        data-testid={`floor-grid-${side}-${floor.position}`}
      />
    );
  };

  return (
    <>
      {stranded.length > 0 && (
        <Alert
          type="warning"
          showIcon
          // A selection change can strand floors, and a warning that appears
          // silently is one a screen-reader user never learns about.
          role="status"
          aria-live="polite"
          style={{ marginBottom: 8 }}
          message={t(
            "scheduledActions.floorGridStranded",
            "This command cannot act on {{floors}}. Those floors are still targeted and cannot be unticked here.",
            { floors: describeFloors(stranded) },
          )}
          action={
            <Button size="small" onClick={dropStranded} data-testid="floor-grid-drop-stranded">
              {t("scheduledActions.floorGridDropStranded", "Remove them")}
            </Button>
          }
        />
      )}
      <div
        style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 2 }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse" }}
          aria-label={t("scheduledActions.floorGridTable", "Target floors and openings")}
        >
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: "left", padding: "6px 8px" }}>
                {t("scheduledActions.floorGridFloor", "Floor")}
              </th>
              {SIDES.map((side) => (
                <th
                  key={side}
                  scope="col"
                  style={{ textAlign: "center", padding: "6px 8px", width: 96 }}
                >
                  {renderHeaderCheckbox(side)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((floor) => (
              <tr key={floor.id}>
                <th scope="row" style={{ padding: "4px 8px", textAlign: "left", fontWeight: 400 }}>
                  {floor.name !== undefined ? (
                    <>
                      {floor.name}{" "}
                      <Typography.Text type="secondary">{`(${floor.position})`}</Typography.Text>
                    </>
                  ) : (
                    floor.position
                  )}
                </th>
                {SIDES.map((side) => (
                  <td key={side} style={{ textAlign: "center", padding: "4px 8px" }}>
                    {renderCell(floor, side)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default FloorOpeningPicker;
